import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import ModelViewer from "../../components/model-viewer/model-viewer";
import WideLayout from "../../layouts/wide-layout";
import toast from "react-hot-toast";

const ModelViewerPage: React.FC = () => {
  const [model, setModel] = useState<File | null>(null);
  const [fileUrl, setFileUrl] = useState<string | undefined>(undefined);
  const [patientId, setPatientId] = useState<string>("???");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!model) return;

    const fileNames = model.name.split(".");
    if (fileNames.length > 1) {
      const words = fileNames[0].split(" ");
      setPatientId(words.length > 1 ? words[1] : "???");
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const url = e.target?.result as string;
      if (!url) {
        console.error("Failed to load model");
        return;
      }
      setFileUrl(url);
    };
    reader.readAsDataURL(model);
  }, [model]);

  const onFileUpload = (file: File | null) => {
    setModel(file);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    const maxFileSizeMB = 20;
    const maxFileSizeBytes = maxFileSizeMB * 1024 * 1024;

    if (!file.name.toLowerCase().endsWith(".ply")) {
      toast.error("Only .ply files are allowed.");
      return;
    }

    // Check file size
    if (file.size > maxFileSizeBytes) {
      toast.error(`File is too large. Maximum allowed size is ${maxFileSizeMB} MB.`);
      return;
    }

    onFileUpload(file);
  };

  return (
    <WideLayout>
      <Stack spacing={3} sx={{ width: "100%", px: 3, boxSizing: "border-box" }}>
        <Stack
          direction={"row"}
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h3">3D Model Viewer</Typography>
          <Button
            variant="contained"
            onClick={handleUploadClick}
            startIcon={<FileUploadIcon />}
            sx={{
              borderRadius: "1.5rem",
              px: 2,
              py: 1,
              height: "40px",
              fontWeight: 600,
            }}
          >
            Upload Model
          </Button>
          <input
            type="file"
            accept=".ply"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleInputChange}
          />
        </Stack>
        <Paper
          elevation={5}
          sx={{
            width: "100%",
            height: "calc(100vh - 200px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "1rem",
          }}
        >
          <ModelViewer
            patientId={patientId}
            modelUrl={fileUrl}
            showDepthEstimation={true}
          />
        </Paper>
      </Stack>
    </WideLayout>
  );
};

export default ModelViewerPage;